/**
 * Data fetching hook for DataList.
 *
 * Uses TanStack React Query to fetch list data from an ERP endpoint.
 * Supports both server-side fetching (via apiClient) and inline data
 * passthrough.  When an endpoint is configured, query parameters are
 * built from the current list state (pagination, sort, search, filters)
 * using `buildListParams`.  The raw API response is mapped to a flat
 * items array and total count via configurable `responseMapping` paths.
 *
 * @see Plan section 3.1 for hook design.
 * @see Spec sections 4.2, 5.1-5.3 for data fetching requirements.
 */

import {
  useMemo,
  useCallback,
  useEffect,
  useSyncExternalStore,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useIntegratedComponentContext } from '../../shared';
import type { DataListConfig, SortState } from '../types';
import { buildListParams } from '../utils/paramBuilder';

// ---------------------------------------------------------------------------
// Response field order per list id — lets a restored sort resolve its
// `sort=<pos>` on the FIRST fetch after remount, before any new response
// arrives. Same SPA-session lifetime as the page/sort stores.
//
// Exposed as a tiny subscribable store so the `queryParams` memo can read the
// order reactively via `useSyncExternalStore` (no `setState`-in-effect, no
// cycle with the downstream response mapping): the snapshot is read at render,
// and a post-fetch write notifies subscribers to recompute. The order is
// stable across fetches to the same endpoint, so the write is a cheap no-op
// (skipped by an equality guard) once it has settled — no render churn.
// ---------------------------------------------------------------------------

const EMPTY_FIELD_ORDER: string[] = [];
const fieldOrderStore = new Map<string, string[]>();
const fieldOrderListeners = new Map<string, Set<() => void>>();

/** Subscribe a listener to changes of one list id's cached field order. */
function subscribeFieldOrder(id: string, listener: () => void): () => void {
  let set = fieldOrderListeners.get(id);
  if (!set) {
    set = new Set();
    fieldOrderListeners.set(id, set);
  }
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

/** Current cached field order for a list id (stable empty array fallback). */
function getFieldOrder(id: string): string[] {
  return fieldOrderStore.get(id) ?? EMPTY_FIELD_ORDER;
}

/**
 * Write `keys` as the field order for `id` and notify subscribers, but only
 * when the order actually changed — keeping the snapshot referentially stable
 * across identical fetches so `useSyncExternalStore` triggers no re-render.
 */
function setFieldOrder(id: string, keys: string[]): void {
  const prev = fieldOrderStore.get(id);
  if (prev && prev.length === keys.length && prev.every((k, i) => k === keys[i])) {
    return;
  }
  fieldOrderStore.set(id, keys);
  const set = fieldOrderListeners.get(id);
  if (set) {
    for (const listener of set) listener();
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Parameters describing the current list state for data fetching.
 */
export interface UseFetchListDataParams {
  page: number;
  pageSize: number;
  sort: SortState[];
  searchTerm: string;
  filterParams: Record<string, unknown>;
}

/**
 * Return value of the `useFetchListData` hook.
 */
export interface UseFetchListDataReturn {
  /** Current page of row data. */
  data: Record<string, unknown>[];
  /** Total number of items across all pages. */
  totalItems: number;
  /** Whether a fetch is currently in-flight. */
  isLoading: boolean;
  /** Whether the very first fetch is in-flight (no cached data yet). */
  isInitialLoading: boolean;
  /** A human-readable error string, or `null` when there is no error. */
  error: string | null;
  /** Manually trigger a data refresh. */
  refresh: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Resolves a value from a nested object using a dot-notation path.
 *
 * @example
 * resolvePathValue({ data: { items: [1, 2] } }, 'data.items') // => [1, 2]
 */
function resolvePathValue(obj: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce(
      (acc, key) => (acc as Record<string, unknown>)?.[key],
      obj,
    );
}

/**
 * Message surfaced for a failure envelope that carries no usable message.
 */
export const FAILURE_ENVELOPE_FALLBACK_MESSAGE =
  'The server reported an error while fetching list data.';

/** Non-blank string, else `undefined`. */
function nonBlank(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

/**
 * Throws when a 2xx response body is a failure envelope.
 *
 * The Origami ERP proxy answers HTTP 200 with
 * `{ success: false, error: { message } }` when the ERP call itself failed.
 * Mapped as a list, that body would yield no items — an error indistinguishable
 * from "no data". Raising it here routes it through the query's error state,
 * so `DataList.Table` shows its error alert. The message is `error.message`,
 * else a string `error`, else a top-level `message`, else
 * {@link FAILURE_ENVELOPE_FALLBACK_MESSAGE}. Any other body is returned as is.
 */
function rejectFailureEnvelope(body: unknown): unknown {
  if (
    typeof body !== 'object' ||
    body === null ||
    Array.isArray(body) ||
    (body as { success?: unknown }).success !== false
  ) {
    return body;
  }
  const { error, message } = body as { error?: unknown; message?: unknown };
  const errorMessage =
    typeof error === 'object' && error !== null
      ? nonBlank((error as { message?: unknown }).message)
      : nonBlank(error);
  throw new Error(errorMessage ?? nonBlank(message) ?? FAILURE_ENVELOPE_FALLBACK_MESSAGE);
}

/**
 * Extracts a human-readable message from an unknown error value.
 */
function extractErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'string') {
    return err;
  }
  return 'An unknown error occurred while fetching list data.';
}

// ---------------------------------------------------------------------------
// Inline data return (no API call)
// ---------------------------------------------------------------------------

const NOOP = (): void => {
  /* intentionally empty */
};

/**
 * Returns a stable result object for inline data mode, bypassing any
 * network request.
 */
function useInlineDataReturn(
  data: Record<string, unknown>[],
): UseFetchListDataReturn {
  return useMemo(
    () => ({
      data,
      totalItems: data.length,
      isLoading: false,
      isInitialLoading: false,
      error: null,
      refresh: NOOP,
    }),
    [data],
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetches list data for the DataList component.
 *
 * When `config.dataSource?.data` is provided (inline data), the hook
 * returns the data directly without making any API call.
 *
 * When `config.dataSource?.endpoint` is configured, the hook:
 * 1. Builds query parameters from the current list state via `buildListParams`.
 * 2. Fetches data using the apiClient from IntegratedComponentContext.
 * 3. Applies `responseMapping` to extract items and total from the response.
 * 4. Supports polling via `config.dataSource?.pollingInterval`.
 *
 * @param config - The full DataList configuration.
 * @param params - The current list state (page, sort, search, filters).
 * @returns An object containing `data`, `totalItems`, loading states, `error`, and `refresh`.
 */
export function useFetchListData(
  config: DataListConfig,
  params: UseFetchListDataParams,
): UseFetchListDataReturn {
  const ctx = useIntegratedComponentContext();
  const apiClient = ctx?.apiClient ?? null;

  // Field order of the last successfully fetched response row, read reactively
  // from the cross-remount store. Used by `buildListParams` to infer a column's
  // numeric sort position (boby's `sort=<pos>` contract). Reading from the
  // store (not local state) means a restored sort resolves its position on the
  // FIRST fetch after remount — the order was cached last session, before any
  // new response arrives. The effect below refreshes it after each fetch.
  const fieldOrder = useSyncExternalStore(
    useCallback(
      (listener: () => void) => subscribeFieldOrder(config.id, listener),
      [config.id],
    ),
    useCallback(() => getFieldOrder(config.id), [config.id]),
  );

  // Build query params from current list state
  const queryParams = useMemo(
    () =>
      buildListParams(
        {
          page: params.page,
          pageSize: params.pageSize,
          sort: params.sort,
          searchTerm: params.searchTerm,
          filterParams: params.filterParams,
        },
        config,
        fieldOrder,
      ),
    [
      params.page,
      params.pageSize,
      params.sort,
      params.searchTerm,
      params.filterParams,
      config,
      // `fieldOrder` is a dep so a restored/default sort resolves its position
      // once the response field order is known (or restored from the store).
      fieldOrder,
    ],
  );

  // Stable serialised key for query params
  const queryParamsSerialized = useMemo(
    () => JSON.stringify(queryParams),
    [queryParams],
  );

  // ---- Endpoint query -------------------------------------------------------

  const endpoint = config.dataSource?.endpoint;
  const method = endpoint?.method ?? 'GET';
  const pollingInterval = config.dataSource?.pollingInterval;

  const queryResult = useQuery<unknown, unknown>({
    queryKey: ['data-list', config.id, queryParamsSerialized],
    queryFn: () => {
      if (!apiClient || !endpoint) {
        return Promise.reject(
          new Error(
            'useFetchListData: query ran without apiClient or endpoint.',
          ),
        );
      }

      if (method === 'POST') {
        return apiClient
          .post(endpoint.erpEndpoint, queryParams)
          .then((res) => rejectFailureEnvelope(res.data));
      }

      return apiClient
        .get(endpoint.erpEndpoint, { params: queryParams })
        .then((res) => rejectFailureEnvelope(res.data));
    },
    refetchInterval: pollingInterval || undefined,
    enabled: Boolean(apiClient) && Boolean(endpoint),
  });

  // ---- Response mapping -----------------------------------------------------

  const mappedData = useMemo<{
    items: Record<string, unknown>[];
    total: number;
  }>(() => {
    if (!queryResult.data) {
      return { items: [], total: 0 };
    }

    const response = queryResult.data;
    const mapping = config.dataSource?.responseMapping;

    let items: unknown;
    let total: number;

    if (mapping?.itemsPath) {
      items = resolvePathValue(response, mapping.itemsPath);
    } else {
      // Default: assume the response itself is the items array,
      // or response.data if present
      items = Array.isArray(response)
        ? response
        : (response as Record<string, unknown>).data ?? response;
    }

    if (!Array.isArray(items)) {
      items = [];
    }

    if (mapping?.totalPath) {
      const rawTotal = resolvePathValue(response, mapping.totalPath);
      total =
        typeof rawTotal === 'number'
          ? rawTotal
          : (items as unknown[]).length;
    } else {
      total = (items as unknown[]).length;
    }

    return {
      items: items as Record<string, unknown>[],
      total,
    };
  }, [queryResult.data, config.dataSource?.responseMapping]);

  // ---- Capture response field order -----------------------------------------
  // Record the field names (in order) of the first row of each successful fetch
  // into the cross-remount store so `buildListParams` can infer a column's
  // numeric sort position. This is an external-store write (mirrors
  // `useListPagination`'s `pageStore.set`), not a `setState`; `setFieldOrder`
  // notifies `useSyncExternalStore` subscribers only when the order actually
  // changed, so a restored/default sort re-resolves once the order is known
  // without any render churn on identical fetches.

  const firstRow = mappedData.items[0];
  useEffect(() => {
    if (!firstRow) return;
    setFieldOrder(config.id, Object.keys(firstRow));
  }, [firstRow, config.id]);

  // ---- Error extraction -----------------------------------------------------

  const error = useMemo<string | null>(() => {
    if (!queryResult.error) {
      return null;
    }
    return extractErrorMessage(queryResult.error);
  }, [queryResult.error]);

  // ---- Refresh callback -----------------------------------------------------

  const { refetch } = queryResult;
  const refresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  // ---- Inline data short-circuit --------------------------------------------

  const inlineReturn = useInlineDataReturn(
    config.dataSource?.data ?? [],
  );

  if (config.dataSource?.data) {
    return inlineReturn;
  }

  // ---- Return ---------------------------------------------------------------

  return {
    data: mappedData.items,
    totalItems: mappedData.total,
    isLoading: queryResult.isLoading,
    isInitialLoading: queryResult.isLoading && !queryResult.data,
    error,
    refresh,
  };
}
